import React, { useEffect, useState, useCallback } from 'react';
import { Pagination, Button, Modal, Input, Spin, Switch } from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import { DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getArticleList, updateArticleStatus } from '../../store/slices/adminSlice';
import { SortableTableHeader } from '../../components/common';
import * as constant from '../../actions/constant';

const { confirm } = Modal;

// TypeScript interfaces
interface ArticleItem {
  _id: string;
  title?: string;
  description?: string;
  isActive: boolean;
  isDeleted?: boolean;
}

// Static configurations
const TABLE_COLUMNS = [
  { key: 'title', label: 'Title', sortable: true, sortKey: 'title' },
  { key: 'status', label: 'Status', sortable: false },
  { key: 'action', label: 'Action', sortable: false },
];

const Article: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  // Redux state
  const { articleData, articleCount, articleLimit, loading } = useAppSelector((state) => state.admin);

  // Local state
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<number | ''>('');
  const [searchText, setSearchText] = useState('');

  // Fetch articles
  const fetchArticles = useCallback((params: Record<string, any> = {}) => {
    dispatch(getArticleList(params));
  }, [dispatch]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Handlers
  const handleSearch = (text: string) => {
    setSearchText(text);
    fetchArticles({ search: text });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchArticles({ page, sortOrder });
  };

  const handleSort = (sortKey: string) => {
    const newOrder = sortOrder === -1 ? 1 : -1;
    setSortOrder(newOrder);
    fetchArticles({ page: currentPage, sortValue: sortKey, sortOrder: newOrder });
  };

  const handleStatusChange = (article: ArticleItem, key: 'isActive' | 'isDeleted') => {
    dispatch(updateArticleStatus({ _id: article._id, [key]: !article[key] }));
  };

  const showDeleteConfirm = (article: ArticleItem) => {
    confirm({
      title: constant.DELETE_RECORD,
      content: '',
      onOk: () => {
        setCurrentPage(1);
        handleStatusChange(article, 'isDeleted');
      },
    });
  };

  const navigateTo = (id?: string) => {
    history.push(id ? `/editearticle/${id}` : '/addearticle');
  };

  // Render table header
  const renderTableHeader = () => (
    <tr>
      {TABLE_COLUMNS.map(({ key, label, sortable, sortKey }) =>
        sortable ? (
          <SortableTableHeader
            key={key}
            label={label}
            sortKey={sortKey || key}
            currentSortOrder={sortOrder}
            onSort={handleSort}
          />
        ) : (
          <th key={key} scope="col">{label}</th>
        )
      )}
    </tr>
  );

  // Render article rows
  const renderArticleRows = () =>
    articleData.map((article: ArticleItem, index: number) => (
      <tr key={article._id || index}>
        <td>{article.title}</td>
        <td>
          <Switch
            checked={article.isActive}
            onChange={() => handleStatusChange(article, 'isActive')}
          />
        </td>
        <td className="action-icons">
          <Button
            type="link"
            className="btn-sm mr-2"
            onClick={() => navigateTo(article._id)}
          >
            <i className="fa fa-pencil" style={{ color: '#666' }} />
          </Button>
          <Button
            type="link"
            className="btn-sm mr-2"
            onClick={() => showDeleteConfirm(article)}
          >
            <DeleteFilled style={{ color: '#666' }} />
          </Button>
        </td>
      </tr>
    ));

  return (
    <div className="box box-default mb-4">
      <PageHeader
        prefixedClassName="ant-page-header"
        title="Article"
        subTitle=""
        extra={[
          <Input
            key="search"
            placeholder="Search"
            className="mobile-bottom-space"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 150 }}
          />,
          <Button key="add" type="default" onClick={() => navigateTo()}>
            <PlusOutlined className="mr-1" /> Add Article
          </Button>,
        ]}
      />
      <Spin spinning={loading} delay={500}>
        <div className="box-body">
          <div className="table-responsive">
            <table className="table">
              <thead>{renderTableHeader()}</thead>
              <tbody>{renderArticleRows()}</tbody>
            </table>
          </div>
          <Pagination
            style={{ textAlign: 'right' }}
            pageSize={articleLimit}
            current={currentPage}
            total={articleCount || 1}
            onChange={handlePageChange}
          />
        </div>
      </Spin>
    </div>
  );
};

export default Article;



